import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Auto-save hook for form drafts
 * 
 * Saves form data to localStorage periodically and on changes,
 * with recovery prompt when reopening with unsaved data.
 * 
 * @param {string} key - Unique key for localStorage
 * @param {Object} initialData - Initial form data
 * @param {Object} options - Configuration options
 * @returns {Object} Hook return values
 */
export const useFormDraft = (
  key,
  initialData = {},
  options = {}
) => {
  const {
    debounceMs = 1000,
    expirationMs = 24 * 60 * 60 * 1000, // 24 hours default
    onRecover = null,
    autoSave = true
  } = options;

  const storageKey = `form_draft_${key}`;
  const [data, setData] = useState(initialData);
  const [hasDraft, setHasDraft] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [recoveryData, setRecoveryData] = useState(null);
  const debounceTimer = useRef(null);

  // Check for existing draft on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const { data: draftData, timestamp } = JSON.parse(stored);
        const now = Date.now();
        
        // Check if draft is expired
        if (now - timestamp > expirationMs) {
          localStorage.removeItem(storageKey);
          return;
        }

        // Only offer recovery if data differs from initial
        const hasChanges = JSON.stringify(draftData) !== JSON.stringify(initialData);
        if (hasChanges) {
          setRecoveryData(draftData);
          setHasDraft(true);
        }
      }
    } catch (err) {
      console.error('Error loading form draft:', err);
      localStorage.removeItem(storageKey);
    }
  }, [storageKey, expirationMs, initialData]);

  // Save draft to localStorage
  const saveDraft = useCallback((formData) => {
    try {
      const draft = {
        data: formData,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(draft));
      setLastSaved(new Date());
      setHasDraft(true);
    } catch (err) {
      console.error('Error saving form draft:', err);
    }
  }, [storageKey]);

  // Debounced save
  const debouncedSave = useCallback((formData) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      saveDraft(formData);
    }, debounceMs);
  }, [saveDraft, debounceMs]);

  // Update data handler
  const updateData = useCallback((newData) => {
    setData(prev => {
      const updated = typeof newData === 'function' ? newData(prev) : { ...prev, ...newData };
      setIsDirty(true);
      
      if (autoSave) {
        debouncedSave(updated);
      }
      
      return updated;
    });
  }, [autoSave, debouncedSave]);

  // Recover draft data
  const recoverDraft = useCallback(() => {
    if (recoveryData) {
      setData(recoveryData);
      setIsDirty(true);
      if (onRecover) {
        onRecover(recoveryData);
      }
      setRecoveryData(null);
    }
  }, [recoveryData, onRecover]);

  // Dismiss recovery prompt
  const dismissRecovery = useCallback(() => {
    setRecoveryData(null);
    localStorage.removeItem(storageKey);
    setHasDraft(false);
  }, [storageKey]);

  // Clear draft (on successful submit)
  const clearDraft = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    localStorage.removeItem(storageKey);
    setHasDraft(false);
    setIsDirty(false);
    setLastSaved(null);
  }, [storageKey]);

  // Force save now
  const saveNow = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    saveDraft(data);
  }, [saveDraft, data]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Save on page unload if dirty
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && autoSave) {
        saveDraft(data);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, autoSave, data, saveDraft]);

  return {
    data,
    setData: updateData,
    isDirty,
    hasDraft,
    lastSaved,
    recoveryData,
    recoverDraft,
    dismissRecovery,
    clearDraft,
    saveNow
  };
};

/**
 * Recovery Prompt Component
 * Displayed when there's a draft to recover
 */
export const DraftRecoveryPrompt = ({
  onRecover,
  onDiscard,
  lastSaved,
  className = ''
}) => {
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className={`draft-recovery-prompt ${className}`}>
      <div className="draft-recovery-content">
        <div className="draft-recovery-icon">📝</div>
        <div className="draft-recovery-text">
          <strong>Unsaved draft found</strong>
          {lastSaved && (
            <span className="draft-recovery-time">
              Last saved {formatTime(lastSaved)}
            </span>
          )}
        </div>
      </div>
      <div className="draft-recovery-actions">
        <button 
          type="button" 
          className="btn-recover"
          onClick={onRecover}
        >
          Restore Draft
        </button>
        <button 
          type="button" 
          className="btn-discard"
          onClick={onDiscard}
        >
          Start Fresh
        </button>
      </div>
    </div>
  );
};

/**
 * Auto-save Status Indicator Component
 * Shows save status in forms
 */
export const AutoSaveStatus = ({
  isDirty,
  lastSaved,
  saving = false,
  className = ''
}) => {
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    
    if (diffMs < 60000) return 'just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    return d.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (saving) {
    return (
      <span className={`autosave-status saving ${className}`}>
        <span className="autosave-icon">⏳</span>
        Saving...
      </span>
    );
  }

  if (!isDirty && lastSaved) {
    return (
      <span className={`autosave-status saved ${className}`}>
        <span className="autosave-icon">✓</span>
        Saved {formatTime(lastSaved)}
      </span>
    );
  }

  if (isDirty) {
    return (
      <span className={`autosave-status unsaved ${className}`}>
        <span className="autosave-icon">●</span>
        Unsaved changes
      </span>
    );
  }

  return null;
};

/**
 * CSS for draft recovery and auto-save status (inline styles fallback)
 * These can be overridden by importing styles separately
 */
export const draftRecoveryStyles = `
  .draft-recovery-prompt {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border: 1px solid #f59e0b;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.2);
  }

  .draft-recovery-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .draft-recovery-icon {
    font-size: 24px;
  }

  .draft-recovery-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .draft-recovery-text strong {
    color: #92400e;
    font-size: 14px;
  }

  .draft-recovery-time {
    color: #b45309;
    font-size: 12px;
  }

  .draft-recovery-actions {
    display: flex;
    gap: 8px;
  }

  .btn-recover {
    padding: 8px 16px;
    background: #f59e0b;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-recover:hover {
    background: #d97706;
  }

  .btn-discard {
    padding: 8px 16px;
    background: transparent;
    color: #92400e;
    border: 1px solid #f59e0b;
    border-radius: 6px;
    font-weight: 500;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-discard:hover {
    background: rgba(245, 158, 11, 0.1);
  }

  .autosave-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 4px;
  }

  .autosave-status.saving {
    color: #6b7280;
    background: #f3f4f6;
  }

  .autosave-status.saved {
    color: #059669;
    background: #ecfdf5;
  }

  .autosave-status.unsaved {
    color: #d97706;
    background: #fffbeb;
  }

  .autosave-icon {
    font-size: 10px;
  }

  @media (max-width: 600px) {
    .draft-recovery-prompt {
      flex-direction: column;
      gap: 12px;
      text-align: center;
    }

    .draft-recovery-content {
      flex-direction: column;
    }
  }
`;

export default useFormDraft;
